import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';
import { getLocale } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import Loader from '@/components/ui/Loader';
import {
  Dialog,
  DialogHeader,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { InputLabel } from '@/components/ui/InputLabel';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import {
  selectExitFlow,
  selectPromoCode,
  selectResumeSubscriptionLoading,
  selectResumeSubscriptionPauseLoading,
  selectSubscription,
} from '@/features/billing/billingSlice';
import {
  acceptRetentionDowngrade,
  acceptRetentionPause,
  confirmRetentionCancellation,
  declineRetentionDowngrade,
  declineRetentionPause,
  fetchPromoCode,
  fetchSubscription,
  fetchUsage,
  resumeSubscription,
  resumeSubscriptionPause,
  startSubscriptionExitFlow,
} from '@/features/billing/billingThunks';
import { fetchWorkspaceById } from '@/features/workspace/workspaceThunks';
import { formatPrice, resolveBillingCurrency } from '@/components/billing/usageExpiredUtils';
import { cn } from '@/lib/utils';
import { Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const CHURN_REASONS = ['ANOTHER_TOOL', 'NO_MORE_WEBINARS', 'TOO_COMPLEX', 'OTHER'] as const;
type ChurnReasonValue = (typeof CHURN_REASONS)[number];
type PauseMonths = 1 | 2 | 3;

type CancelSubscriptionFormValues = {
  reasonKey: ChurnReasonValue | '';
  details: string;
};

export default function FlowCancelSubscribs() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation('settings');
  const workspaceId = useAppSelector((s: RootState) => s.workspace.currentWorkspace?.id);
  const subscription = useAppSelector(selectSubscription);
  const workspacePromoCode = useAppSelector(
    (s: RootState) => s.workspace.currentWorkspace?.promoCode,
  );
  const promoCode = useAppSelector(selectPromoCode);
  const billingCurrency = resolveBillingCurrency(
    useAppSelector((s: RootState) => s.workspace.currentWorkspace?.billingCurrency),
  );
  const isWorkspaceTrial = useAppSelector(
    (s: RootState) => s.workspace.currentWorkspace?.subscriptionStatus === 'NONE',
  );
  const isInTrial =
    isWorkspaceTrial || subscription?.status === 'trialing' || !subscription?.planName;
  const exitFlow = useAppSelector(selectExitFlow);
  const exitFlowLoading = useAppSelector((s: RootState) => s.billing.exitFlow.loading);
  const resumeLoading = useAppSelector(selectResumeSubscriptionLoading);
  const resumePauseLoading = useAppSelector(selectResumeSubscriptionPauseLoading);
  const isCancellationScheduled = subscription?.cancelAtPeriodEnd === true;
  const isPauseScheduled =
    subscription?.pause?.status === 'SCHEDULED' && subscription.pause.canResume;
  const [open, setOpen] = useState(false);
  const [pauseMonths, setPauseMonths] = useState<PauseMonths>(1);
  const [isStartingFlow, setIsStartingFlow] = useState(false);
  const [isAcceptingDowngrade, setIsAcceptingDowngrade] = useState(false);
  const [isDecliningDowngrade, setIsDecliningDowngrade] = useState(false);
  const [isAcceptingPause, setIsAcceptingPause] = useState(false);
  const [isDecliningPause, setIsDecliningPause] = useState(false);

  const cancelSubscriptionFormSchema = useMemo(
    () =>
      z
        .object({
          reasonKey: z.union([z.enum(CHURN_REASONS), z.literal('')]),
          details: z.string().max(2000, t('cancelSubscription.finalStep.validation.detailsMax')),
        })
        .superRefine((data, ctx) => {
          if (data.reasonKey === '') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('cancelSubscription.finalStep.validation.reasonRequired'),
              path: ['reasonKey'],
            });
            return;
          }
          if (data.reasonKey === 'ANOTHER_TOOL' || data.reasonKey === 'OTHER') {
            if (!data.details.trim()) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                  data.reasonKey === 'ANOTHER_TOOL'
                    ? t('cancelSubscription.finalStep.validation.anotherToolDetail')
                    : t('cancelSubscription.finalStep.validation.otherDetail'),
                path: ['details'],
              });
            }
          }
        }),
    [t],
  );

  const form = useForm<CancelSubscriptionFormValues>({
    resolver: zodResolver(cancelSubscriptionFormSchema),
    defaultValues: {
      reasonKey: '',
      details: '',
    },
    mode: 'onChange',
  });

  const reasonKey = form.watch('reasonKey');
  const submitting = form.formState.isSubmitting || exitFlowLoading;

  useEffect(() => {
    if (reasonKey !== 'ANOTHER_TOOL' && reasonKey !== 'OTHER') {
      form.setValue('details', '');
      form.clearErrors('details');
    }
  }, [reasonKey, form]);

  const showHiddenDowngradeStep =
    open &&
    exitFlow?.currentStep === 'HIDDEN_DOWNGRADE' &&
    (exitFlow.productAccessStatus === 'FULL_ACCESS' || exitFlow.status === 'IN_PROGRESS');

  useEffect(() => {
    if (showHiddenDowngradeStep && workspaceId && workspacePromoCode) {
      dispatch(fetchPromoCode({ workspaceId, code: workspacePromoCode }));
    }
  }, [showHiddenDowngradeStep, workspaceId, workspacePromoCode, dispatch]);
  const RETENTION_PLAN_BASE_AMOUNT = 4900;
  const retentionDiscountAmount =
    promoCode?.active && workspacePromoCode
      ? promoCode.percentOff != null
        ? Math.round((RETENTION_PLAN_BASE_AMOUNT * promoCode.percentOff) / 100)
        : (promoCode.amountOff ?? 0)
      : 0;
  const retentionFinalAmount = Math.max(0, RETENTION_PLAN_BASE_AMOUNT - retentionDiscountAmount);

  const showFreePauseStep = open && exitFlow?.currentStep === 'FREE_PAUSE';
  const showFinalCancellationStep = open && exitFlow?.currentStep === 'FINAL_CANCELLATION';

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (submitting && !next) return;
      setOpen(next);
      if (!next) {
        form.reset({ reasonKey: '', details: '' });
        setPauseMonths(1);
      }
    },
    [form, submitting],
  );

  const handleCancelClick = useCallback(async () => {
    if (!workspaceId || isStartingFlow) return;

    setIsStartingFlow(true);
    try {
      const result = await dispatch(startSubscriptionExitFlow(workspaceId)).unwrap();
      const shouldShowDowngradeStep =
        result.currentStep === 'HIDDEN_DOWNGRADE' &&
        (result.productAccessStatus === 'FULL_ACCESS' || result.status === 'IN_PROGRESS');
      const shouldShowPauseStep = result.currentStep === 'FREE_PAUSE';
      const shouldShowFinalCancellationStep = result.currentStep === 'FINAL_CANCELLATION';

      if (shouldShowDowngradeStep || shouldShowPauseStep || shouldShowFinalCancellationStep) {
        setOpen(true);
      }
    } catch (e: unknown) {
      const err = e as { message?: string };
      toast.error(err?.message ?? t('cancelSubscription.toast.startFlowError'));
    } finally {
      setIsStartingFlow(false);
    }
  }, [dispatch, workspaceId, isStartingFlow, t]);

  const handleAcceptDowngrade = useCallback(async () => {
    if (!workspaceId || isAcceptingDowngrade) return;

    setIsAcceptingDowngrade(true);
    try {
      await dispatch(acceptRetentionDowngrade(workspaceId)).unwrap();
      await Promise.all([
        dispatch(fetchSubscription(workspaceId)).unwrap(),
        dispatch(fetchUsage(workspaceId)).unwrap(),
        dispatch(fetchWorkspaceById(workspaceId)).unwrap(),
      ]);
      toast.success(t('cancelSubscription.toast.planChanged'));
      setOpen(false);
    } catch (e: unknown) {
      const err = e as { message?: string };
      toast.error(err?.message ?? t('cancelSubscription.toast.changePlanError'));
    } finally {
      setIsAcceptingDowngrade(false);
    }
  }, [dispatch, workspaceId, isAcceptingDowngrade, t]);

  const handleDeclineDowngrade = useCallback(async () => {
    if (!workspaceId || isDecliningDowngrade) return;

    setIsDecliningDowngrade(true);
    try {
      await dispatch(declineRetentionDowngrade(workspaceId)).unwrap();
    } catch (e: unknown) {
      const err = e as { message?: string };
      toast.error(err?.message ?? t('cancelSubscription.toast.nextStepError'));
    } finally {
      setIsDecliningDowngrade(false);
    }
  }, [dispatch, workspaceId, isDecliningDowngrade, t]);

  const handleAcceptPause = useCallback(async () => {
    if (!workspaceId || isAcceptingPause) return;

    setIsAcceptingPause(true);
    try {
      await dispatch(
        acceptRetentionPause({ workspaceId, input: { months: pauseMonths } }),
      ).unwrap();
      await Promise.all([
        dispatch(fetchSubscription(workspaceId)).unwrap(),
        dispatch(fetchUsage(workspaceId)).unwrap(),
        dispatch(fetchWorkspaceById(workspaceId)).unwrap(),
      ]);
      toast.success(t('cancelSubscription.toast.pauseSuccess'));
      setOpen(false);
      setPauseMonths(1);
      navigate('/', { replace: true });
    } catch (e: unknown) {
      const err = e as { message?: string };
      toast.error(err?.message ?? t('cancelSubscription.toast.pauseError'));
    } finally {
      setIsAcceptingPause(false);
    }
  }, [dispatch, workspaceId, isAcceptingPause, pauseMonths, navigate, t]);

  const handleDeclinePause = useCallback(async () => {
    if (!workspaceId || isDecliningPause) return;

    setIsDecliningPause(true);
    try {
      await dispatch(declineRetentionPause(workspaceId)).unwrap();
    } catch (e: unknown) {
      const err = e as { message?: string };
      toast.error(err?.message ?? t('cancelSubscription.toast.nextStepError'));
    } finally {
      setIsDecliningPause(false);
    }
  }, [dispatch, workspaceId, isDecliningPause, t]);

  const handleResumeSubscription = useCallback(async () => {
    if (!workspaceId) return;

    try {
      await dispatch(resumeSubscription(workspaceId)).unwrap();
    } catch (e: unknown) {
      const err = e as { message?: string };
      toast.error(err?.message ?? t('cancelSubscription.toast.resumeError'));
    }
  }, [dispatch, workspaceId, t]);

  const handleResumePause = useCallback(async () => {
    if (!workspaceId) return;

    try {
      await dispatch(resumeSubscriptionPause(workspaceId)).unwrap();
      toast.success(t('cancelSubscription.toast.resumePauseSuccess'));
    } catch (e: unknown) {
      const err = e as { message?: string };
      toast.error(err?.message ?? t('cancelSubscription.toast.resumePauseError'));
    }
  }, [dispatch, workspaceId, t]);

  const handleConfirmRetentionCancellation = form.handleSubmit(async (values) => {
    if (!workspaceId || values.reasonKey === '') return;

    try {
      const updatedFlow = await dispatch(
        confirmRetentionCancellation({
          workspaceId,
          input: {
            reasonKey: values.reasonKey,
            details:
              values.reasonKey === 'ANOTHER_TOOL' || values.reasonKey === 'OTHER'
                ? values.details.trim()
                : undefined,
          },
        }),
      ).unwrap();

      const updatedSubscription = await dispatch(fetchSubscription(workspaceId)).unwrap();
      await Promise.all([
        dispatch(fetchUsage(workspaceId)),
        dispatch(fetchWorkspaceById(workspaceId)),
      ]);

      const cancelAtRaw =
        updatedSubscription.cancelAt != null && updatedSubscription.cancelAt !== ''
          ? updatedSubscription.cancelAt
          : updatedSubscription.currentPeriodEnd;
      const cancelAtFormatted = cancelAtRaw
        ? new Date(cancelAtRaw).toLocaleDateString(getLocale(), {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })
        : null;

      if (cancelAtFormatted) {
        toast.success(t('cancelSubscription.toast.cancelWithDate', { date: cancelAtFormatted }));
      } else if (updatedFlow.status === 'CANCELLATION_CONFIRMED') {
        toast.success(t('cancelSubscription.toast.cancelSuccess'));
      }

      setOpen(false);
      form.reset({ reasonKey: '', details: '' });
      if (isInTrial) {
        navigate('/', { replace: true });
      }
    } catch (e: unknown) {
      const err = e as { message?: string };
      toast.error(err?.message ?? t('cancelSubscription.toast.cancelError'));
    }
  });

  if (isPauseScheduled) {
    return (
      <button
        type="button"
        className="text-secondary cursor-pointer disabled:pointer-events-none disabled:opacity-50"
        disabled={resumePauseLoading || !workspaceId}
        onClick={handleResumePause}
      >
        {t('cancelSubscription.resumePause')}
      </button>
    );
  }

  if (isCancellationScheduled) {
    return (
      <button
        type="button"
        className="text-secondary cursor-pointer disabled:pointer-events-none disabled:opacity-50"
        disabled={resumeLoading || !workspaceId}
        onClick={handleResumeSubscription}
      >
        {t('cancelSubscription.resumeCancel')}
      </button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <button
        type="button"
        className="text-secondary cursor-pointer disabled:pointer-events-none disabled:opacity-50"
        disabled={isStartingFlow || !workspaceId}
        onClick={handleCancelClick}
      >
        {t('cancelSubscription.cancel')}
      </button>

      {showHiddenDowngradeStep && (
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('cancelSubscription.downgrade.title')}</DialogTitle>
          </DialogHeader>
          <p className="text-secondary">{t('cancelSubscription.downgrade.warning')}</p>

          <div className="flex flex-col gap-6 p-4 border border-input rounded-2xl">
            <div>
              <h2 className="text-2xl font-semibold">
                {t('cancelSubscription.downgrade.alternativeTitle')}
                <br />
                <span className="text-highlighted">
                  {t('cancelSubscription.downgrade.alternativeSuffix')}
                </span>
              </h2>
              <p className="text-secondary text-sm pt-2">
                {t('cancelSubscription.downgrade.alternativeDesc')}
              </p>
            </div>

            {retentionDiscountAmount > 0 ? (
              <div className="flex items-baseline gap-2">
                <span className="text-base line-through text-tertiary">
                  {formatPrice(RETENTION_PLAN_BASE_AMOUNT, billingCurrency)}
                </span>
                <span className="text-2xl text-highlighted font-semibold">
                  {formatPrice(retentionFinalAmount, billingCurrency)}
                  {t('cancelSubscription.downgrade.priceSuffix')}
                </span>
              </div>
            ) : (
              <p className="text-2xl text-highlighted font-semibold">
                {t('cancelSubscription.downgrade.price', {
                  amount: formatPrice(RETENTION_PLAN_BASE_AMOUNT, billingCurrency),
                })}
              </p>
            )}

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Check size={16} className="text-success" />
                <p className="text-secondary">{t('cancelSubscription.downgrade.feature1')}</p>
              </div>
              <div className="flex items-center gap-2">
                <Check size={16} className="text-success" />
                <p className="text-secondary">{t('cancelSubscription.downgrade.feature2')}</p>
              </div>
              <div className="flex items-center gap-2">
                <Check size={16} className="text-success" />
                <p className="text-secondary">{t('cancelSubscription.downgrade.feature3')}</p>
              </div>
              <div className="flex items-center gap-2">
                <X size={16} className="text-disable" />
                <p className="text-secondary">{t('cancelSubscription.downgrade.noLive')}</p>
              </div>
              <div className="flex items-center gap-2">
                <X size={16} className="text-disable" />
                <p className="text-secondary">{t('cancelSubscription.downgrade.noEvergreen')}</p>
              </div>
            </div>
            <div className="w-full">
              <Button
                className="w-full h-12"
                disabled={isAcceptingDowngrade || isDecliningDowngrade}
                onClick={handleAcceptDowngrade}
              >
                {isAcceptingDowngrade && <Loader />}
                {t('cancelSubscription.downgrade.downgradeButton')}
              </Button>
            </div>
          </div>
          <div>
            <button
              type="button"
              className="font-medium cursor-pointer disabled:pointer-events-none disabled:opacity-50"
              disabled={isAcceptingDowngrade || isDecliningDowngrade}
              onClick={handleDeclineDowngrade}
            >
              {t('cancelSubscription.downgrade.continueCancel')}
            </button>
          </div>
        </DialogContent>
      )}

      {showFreePauseStep && (
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('cancelSubscription.pause.title')}</DialogTitle>
          </DialogHeader>
          <p className="text-secondary">{t('cancelSubscription.pause.warning')}</p>

          <div className="flex flex-col gap-6 p-4 border border-input rounded-2xl">
            <div>
              <h2 className="text-2xl font-semibold">
                {t('cancelSubscription.pause.alternativeTitle')}
              </h2>
              <p className="text-secondary text-sm pt-2">
                {t('cancelSubscription.pause.alternativeDesc')}
              </p>
            </div>

            <p className="text-2xl text-highlighted font-semibold">
              {t('cancelSubscription.pause.price', { amount: formatPrice(0, billingCurrency) })}
            </p>

            <div className="flex flex-col gap-2">
              <Label htmlFor="pause-months">{t('cancelSubscription.pause.pauseDuration')}</Label>
              <Select
                value={String(pauseMonths)}
                onValueChange={(value) => setPauseMonths(Number(value) as PauseMonths)}
                disabled={isAcceptingPause || isDecliningPause}
              >
                <SelectTrigger id="pause-months" className="w-full">
                  <SelectValue placeholder={t('cancelSubscription.pause.pausePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">{t('cancelSubscription.pause.month1')}</SelectItem>
                  <SelectItem value="2">{t('cancelSubscription.pause.month2')}</SelectItem>
                  <SelectItem value="3">{t('cancelSubscription.pause.month3')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Check size={16} className="text-success" />
                <p className="text-secondary">{t('cancelSubscription.pause.feature1')}</p>
              </div>
              <div className="flex items-center gap-2">
                <X size={16} className="text-disable" />
                <p className="text-secondary">{t('cancelSubscription.pause.noAccess')}</p>
              </div>
            </div>
            <div className="w-full">
              <Button
                className="w-full h-12"
                disabled={isAcceptingPause || isDecliningPause}
                onClick={handleAcceptPause}
              >
                {isAcceptingPause && <Loader />}
                {t('cancelSubscription.pause.pauseButton')}
              </Button>
            </div>
          </div>
          <div className="flex justify-center">
            <button
              type="button"
              className="font-medium cursor-pointer disabled:pointer-events-none disabled:opacity-50"
              disabled={isAcceptingPause || isDecliningPause}
              onClick={handleDeclinePause}
            >
              {t('cancelSubscription.pause.continueCancel')}
            </button>
          </div>
        </DialogContent>
      )}

      {showFinalCancellationStep && (
        <DialogContent
          hideCloseButton={submitting}
          onEscapeKeyDown={submitting ? (e) => e.preventDefault() : undefined}
          onPointerDownOutside={submitting ? (e) => e.preventDefault() : undefined}
        >
          <DialogHeader>
            <DialogTitle>{t('cancelSubscription.finalStep.title')}</DialogTitle>
            <DialogDescription className="text-base">
              {t('cancelSubscription.finalStep.description')}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleConfirmRetentionCancellation} className="flex flex-col gap-0">
            <div className="flex flex-col gap-4 py-6">
              <Controller
                name="reasonKey"
                control={form.control}
                render={({ field }) => (
                  <div className="flex flex-col gap-3">
                    <RadioGroup
                      value={field.value === '' ? undefined : field.value}
                      onValueChange={(v) => field.onChange(v as ChurnReasonValue)}
                      disabled={submitting}
                      className="flex flex-col gap-6"
                    >
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="ANOTHER_TOOL" id="cancel-reason-other-tool" />
                          <Label
                            htmlFor="cancel-reason-other-tool"
                            className="cursor-pointer font-normal"
                          >
                            {t('cancelSubscription.finalStep.reason1')}
                          </Label>
                        </div>
                        {reasonKey === 'ANOTHER_TOOL' && (
                          <Controller
                            name="details"
                            control={form.control}
                            render={({ field: detailsField }) => (
                              <div className="w-full">
                                <InputLabel
                                  id="cancel-details-another-tool"
                                  label={t('cancelSubscription.finalStep.reason1Detail')}
                                  value={detailsField.value}
                                  onChange={(e) => detailsField.onChange(e.target.value)}
                                  disabled={submitting}
                                  maxLength={2000}
                                  className={cn(
                                    'w-full',
                                    form.formState.errors.details && 'border-destructive',
                                  )}
                                />
                                {form.formState.errors.details && (
                                  <p className="text-destructive mt-1 text-sm">
                                    {form.formState.errors.details.message}
                                  </p>
                                )}
                              </div>
                            )}
                          />
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="NO_MORE_WEBINARS" id="cancel-reason-no-webinars" />
                        <Label
                          htmlFor="cancel-reason-no-webinars"
                          className="cursor-pointer font-normal"
                        >
                          {t('cancelSubscription.finalStep.reason2')}
                        </Label>
                      </div>

                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="TOO_COMPLEX" id="cancel-reason-too-complicated" />
                        <Label
                          htmlFor="cancel-reason-too-complicated"
                          className="cursor-pointer font-normal"
                        >
                          {t('cancelSubscription.finalStep.reason3')}
                        </Label>
                      </div>

                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="OTHER" id="cancel-reason-other" />
                          <Label
                            htmlFor="cancel-reason-other"
                            className="cursor-pointer font-normal"
                          >
                            {t('cancelSubscription.finalStep.reason4')}
                          </Label>
                        </div>
                        {reasonKey === 'OTHER' && (
                          <Controller
                            name="details"
                            control={form.control}
                            render={({ field: detailsField }) => (
                              <div className="w-full">
                                <InputLabel
                                  id="cancel-details-other"
                                  label={t('cancelSubscription.finalStep.reason4Detail')}
                                  value={detailsField.value}
                                  onChange={(e) => detailsField.onChange(e.target.value)}
                                  disabled={submitting}
                                  maxLength={2000}
                                  className={cn(
                                    'w-full',
                                    form.formState.errors.details && 'border-destructive',
                                  )}
                                />
                                {form.formState.errors.details && (
                                  <p className="text-destructive mt-1 text-sm">
                                    {form.formState.errors.details.message}
                                  </p>
                                )}
                              </div>
                            )}
                          />
                        )}
                      </div>
                    </RadioGroup>
                    {form.formState.errors.reasonKey && (
                      <p className="text-destructive text-sm">
                        {form.formState.errors.reasonKey.message}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>

            <DialogFooter className="flex w-full gap-2">
              <div className="w-1/2">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12"
                  disabled={submitting}
                  onClick={() => handleOpenChange(false)}
                >
                  {t('cancelSubscription.finalStep.cancelButton')}
                </Button>
              </div>
              <div className="w-1/2">
                <Button type="submit" className="w-full h-12" disabled={submitting || !workspaceId}>
                  {submitting && <Loader />}
                  {t('cancelSubscription.finalStep.confirmButton')}
                </Button>
              </div>
            </DialogFooter>
          </form>
          <p className="text-secondary text-sm">{t('cancelSubscription.finalStep.emailNotice')}</p>
        </DialogContent>
      )}
    </Dialog>
  );
}
